import React, { useState, FormEvent, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Eye, EyeOff, Shield } from 'lucide-react';
import { getUserInfo, saveUserInfo, UserInfo, changePassword } from '../../utils/auth';

interface FormErrors {
  [key: string]: string;
}

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const [showCurrentPassword, setShowCurrentPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [errors, setErrors] = useState<FormErrors>({});

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.oldPassword) {
      newErrors.oldPassword = '현재 비밀번호를 입력해주세요';
    }
    
    if (!formData.newPassword) {
      newErrors.newPassword = '신규 비밀번호를 입력해주세요';
    } else if (formData.newPassword.length < 8 || formData.newPassword.length > 20) {
      newErrors.newPassword = '비밀번호는 8-20자 사이여야 합니다';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = '비밀번호 확인을 입력해주세요';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = '비밀번호가 일치하지 않습니다';
    }
    
    if (formData.oldPassword === formData.newPassword) {
      newErrors.newPassword = '현재 비밀번호와 동일한 비밀번호는 사용할 수 없습니다';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const userInfo = getUserInfo();
    if (!userInfo) {
      alert('사용자 정보가 없습니다. 다시 로그인해주세요.');
      navigate('/auth/login');
      return;
    }
    
    setIsLoading(true);
    
    try {
      console.log('비밀번호 변경 시도:', {
        accountCode: userInfo.accountCode,
        userId: userInfo.userId,
        userType: userInfo.userType
      });

      await changePassword(
        userInfo.accountCode,
        userInfo.userId,
        userInfo.userType,
        formData.oldPassword,
        formData.newPassword
      );
      
      alert('비밀번호가 성공적으로 변경되었습니다.');
      // 비밀번호 변경 후 메인 페이지로 이동
      navigate('/main');
    } catch (error) {
      console.error('비밀번호 변경 오류:', error);
      // 서버에서 보낸 에러 메시지 우선 사용
      const serverMessage = error instanceof Error ? error.message : '';
      const defaultMessage = '비밀번호 변경에 실패했습니다. 다시 시도해주세요.';
      const fullMessage = serverMessage || defaultMessage;
      alert(fullMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = (): void => {
    // 로그인 페이지로 이동
    navigate('/auth/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div 
              className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate('/')}
            >
              <img src="/flos-icon.svg" alt="Flos" className="w-8 h-8 mr-2" />
              <span className="text-xl font-semibold text-gray-900">Flos</span>
            </div>
            <button
              onClick={handleBack}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-1" />
              <span>돌아가기</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
                <Shield className="w-8 h-8 text-orange-600" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">비밀번호 변경</h1>
            <p className="mt-2 text-gray-600">보안을 위해 비밀번호를 변경해주세요</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6">
              <form onSubmit={handleSubmit} method="post" autoComplete="off">
                <div className="space-y-4">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <p className="text-sm text-orange-800">
                      보안 정책에 따라 비밀번호 변경이 필요합니다. 안전한 비밀번호로 변경해주세요.
                    </p>
                  </div>

                  <div>
                    <label htmlFor="oldPassword" className="block text-sm font-medium text-gray-700 mb-2">
                      현재 비밀번호
                    </label>
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        id="oldPassword"
                        name="oldPassword"
                        value={formData.oldPassword}
                        onChange={handleInputChange}
                        autoComplete="current-password"
                        className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 ${
                          errors.oldPassword ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="현재 비밀번호 입력"
                        maxLength={50}
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {errors.oldPassword && (
                      <p className="mt-1 text-sm text-red-500 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.oldPassword}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                      신규 비밀번호
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        id="newPassword"
                        name="newPassword"
                        value={formData.newPassword}
                        onChange={handleInputChange}
                        autoComplete="new-password"
                        className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 ${
                          errors.newPassword ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="8-20자 입력"
                        maxLength={50}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {errors.newPassword && (
                      <p className="mt-1 text-sm text-red-500 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.newPassword}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">8-20자 사이의 비밀번호를 입력해주세요</p>
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                      비밀번호 확인
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        autoComplete="new-password"
                        className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 ${
                          errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="비밀번호 재입력"
                        maxLength={50}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <p className="mt-1 text-sm text-red-500 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.confirmPassword}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="mt-6 space-y-3">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className={`w-full py-3 bg-gray-900 text-white rounded-lg transition-colors flex items-center justify-center ${
                        isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800'
                      }`}
                    >
                      {isLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          변경 중...
                        </>
                      ) : (
                        '비밀번호 변경'
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Security Notice */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              보안을 위해 정기적으로 비밀번호를 변경해주세요.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;